import { z } from 'zod';
import { ConfirmationMessageTypeEnum } from './confirmation.messages.types';
import type {
  ExternalToolResultItem,
  TerminalOutputItem,
} from './confirmation.messages.types';
import {
  TERMINAL_EXECUTION_TOOL_NAME,
  isTerminalToolName,
} from './external-tool.types';
import { getPortalToolOutputSchema } from './portal-tools/portal-tool-output-registry';
import { parsePortalToolResultItem } from './portal-tools/portal-tool.schemas';

export const ExternalToolInterruptPayloadSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'object' || value === null) {
      return value;
    }

    const record = value as Record<string, unknown>;
    const sessionId =
      typeof record.sessionId === 'string' ? record.sessionId : undefined;
    const command =
      typeof record.command === 'string' ? record.command : undefined;

    return {
      ...record,
      batchId: typeof record.batchId === 'string' ? record.batchId : sessionId,
      chatId: typeof record.chatId === 'string' ? record.chatId : sessionId,
      displayCommand:
        typeof record.displayCommand === 'string'
          ? record.displayCommand
          : command,
    };
  },
  z.object({
    toolCallId: z.string(),
    toolName: z.string(),
    sessionId: z.string(),
    command: z.string().optional(),
    batchId: z.string(),
    chatId: z.string(),
    displayCommand: z.string().optional(),
    resumeGroupId: z.string().optional(),
  }),
);

export type ExternalToolInterruptPayload = z.infer<
  typeof ExternalToolInterruptPayloadSchema
>;

export const GraphInterruptRecordSchema = z.object({
  id: z.string(),
  value: z.unknown(),
});

export type GraphInterruptRecord = z.infer<typeof GraphInterruptRecordSchema>;

export const TerminalFailureKindSchema = z.enum([
  'command',
  'timeout',
  'abort',
  'tracker',
  'transport',
]);

export const TerminalJobStatusSchema = z.enum(['running', 'exited', 'killed']);

export const TerminalOutputItemSchema = z.object({
  batchId: z.string().optional(),
  toolCallId: z.string(),
  command: z.string(),
  exitCode: z.number(),
  output: z.string(),
  outputLength: z.number(),
  isTimeout: z.boolean().optional(),
  failureKind: TerminalFailureKindSchema.optional(),
  timedOut: z.boolean().optional(),
  idleTimedOut: z.boolean().optional(),
  backgrounded: z.boolean().optional(),
  jobId: z.string().optional(),
  jobStatus: TerminalJobStatusSchema.optional(),
  logPath: z.string().optional(),
  cwdAfter: z.string().optional(),
  durationMs: z.number().optional(),
  truncated: z.boolean().optional(),
  outputBytes: z.number().optional(),
  shell: z.string().optional(),
  platform: z.string().optional(),
  ptyId: z.string().optional(),
  ptyAlive: z.boolean().optional(),
  screen: z.string().optional(),
});

export const TerminalOutputResumeSchema = TerminalOutputItemSchema.extend({
  type: z.literal(ConfirmationMessageTypeEnum.TerminalOutput),
});

export const ExternalToolResultItemSchema = z
  .object({
    batchId: z.string().optional(),
    toolCallId: z.string(),
    toolName: z.string(),
    status: z.enum(['success', 'error']),
    result: z.unknown(),
  })
  .refine((item) => Object.prototype.hasOwnProperty.call(item, 'result'))
  .transform(
    (item): ExternalToolResultItem => ({
      batchId: item.batchId,
      toolCallId: item.toolCallId,
      toolName: item.toolName,
      status: item.status,
      result: item.result,
    }),
  );

export const ExternalToolResultsResumeSchema = z.object({
  type: z.literal(ConfirmationMessageTypeEnum.ExternalToolResults),
  batchId: z.string(),
  results: z.array(ExternalToolResultItemSchema),
});

const LegacyExternalToolInterruptPayloadSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string().optional(),
  sessionId: z.string(),
  command: z.string().optional(),
  batchId: z.string().optional(),
  chatId: z.string().optional(),
  displayCommand: z.string().optional(),
  resumeGroupId: z.string().optional(),
});

const InterruptValueWithExternalPayloadSchema = z.object({
  additional_kwargs: z
    .object({
      externalToolInterrupt: ExternalToolInterruptPayloadSchema.optional(),
      portalToolInterrupt: LegacyExternalToolInterruptPayloadSchema.optional(),
      terminalInterrupt: LegacyExternalToolInterruptPayloadSchema.optional(),
    })
    .optional(),
  externalToolInterrupt: ExternalToolInterruptPayloadSchema.optional(),
  portalToolInterrupt: LegacyExternalToolInterruptPayloadSchema.optional(),
  terminalInterrupt: LegacyExternalToolInterruptPayloadSchema.optional(),
});

function resolveExternalToolInterruptPayload({
  payload,
  defaultToolName,
}: {
  payload: z.infer<typeof LegacyExternalToolInterruptPayloadSchema> | undefined;
  defaultToolName?: string;
}): ExternalToolInterruptPayload | undefined {
  if (!payload?.toolCallId || !payload.sessionId) {
    return undefined;
  }

  const toolName = payload.toolName ?? defaultToolName;

  if (!toolName) {
    return undefined;
  }

  const parsed = ExternalToolInterruptPayloadSchema.safeParse({
    toolCallId: payload.toolCallId,
    toolName,
    sessionId: payload.sessionId,
    ...(typeof payload.command === 'string'
      ? { command: payload.command }
      : {}),
    ...(typeof payload.batchId === 'string'
      ? { batchId: payload.batchId }
      : {}),
    ...(typeof payload.chatId === 'string' ? { chatId: payload.chatId } : {}),
    ...(typeof payload.displayCommand === 'string'
      ? { displayCommand: payload.displayCommand }
      : {}),
    ...(typeof payload.resumeGroupId === 'string'
      ? { resumeGroupId: payload.resumeGroupId }
      : {}),
  });

  return parsed.success ? parsed.data : undefined;
}

const interruptMessageRecord = (value: unknown): unknown => {
  if (!value || typeof value !== 'object') {
    return value;
  }

  const record = value as Record<string, unknown>;
  const nested = [record.kwargs, record.lc_kwargs, record.data];

  for (const candidate of nested) {
    if (!candidate || typeof candidate !== 'object') {
      continue;
    }

    const additionalKwargs = (candidate as Record<string, unknown>)
      .additional_kwargs;

    if (additionalKwargs && typeof additionalKwargs === 'object') {
      return candidate;
    }
  }

  return value;
};

export function parseExternalToolInterruptPayload(
  value: unknown,
): ExternalToolInterruptPayload | undefined {
  const parsed = InterruptValueWithExternalPayloadSchema.safeParse(
    interruptMessageRecord(value),
  );

  if (!parsed.success) {
    return undefined;
  }

  const kwargs = parsed.data.additional_kwargs;
  const candidates = [
    kwargs?.externalToolInterrupt,
    parsed.data.externalToolInterrupt,
    resolveExternalToolInterruptPayload({
      payload: kwargs?.portalToolInterrupt,
    }),
    resolveExternalToolInterruptPayload({
      payload: parsed.data.portalToolInterrupt,
    }),
    resolveExternalToolInterruptPayload({
      payload: kwargs?.terminalInterrupt,
      defaultToolName: TERMINAL_EXECUTION_TOOL_NAME,
    }),
    resolveExternalToolInterruptPayload({
      payload: parsed.data.terminalInterrupt,
      defaultToolName: TERMINAL_EXECUTION_TOOL_NAME,
    }),
  ];

  for (const candidate of candidates) {
    if (candidate) {
      return candidate;
    }
  }

  return undefined;
}

export function parseTerminalInterruptField(
  value: unknown,
): ExternalToolInterruptPayload | undefined {
  const parsed = LegacyExternalToolInterruptPayloadSchema.safeParse(value);

  if (!parsed.success) {
    return undefined;
  }

  return resolveExternalToolInterruptPayload({
    payload: parsed.data,
    defaultToolName: TERMINAL_EXECUTION_TOOL_NAME,
  });
}

export function parseExternalToolInterruptField(
  value: unknown,
): ExternalToolInterruptPayload | undefined {
  const parsed = ExternalToolInterruptPayloadSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
}

export const NeoBackgroundInterruptPayloadSchema = z.object({
  neoThreadId: z.string(),
  toolCallId: z.string(),
  copilotThreadId: z.string(),
});

export type NeoBackgroundInterruptPayload = z.infer<
  typeof NeoBackgroundInterruptPayloadSchema
>;

export function parseNeoBackgroundInterruptField(
  value: unknown,
): NeoBackgroundInterruptPayload | undefined {
  const parsed = NeoBackgroundInterruptPayloadSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
}

export function parseTerminalOutputItem(
  value: unknown,
): TerminalOutputItem | undefined {
  const parsed = TerminalOutputItemSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
}

export function validateExternalToolResultItem(
  item: ExternalToolResultItem,
): boolean {
  if (isTerminalToolName(item.toolName)) {
    const parsed = parseTerminalOutputItem(item.result);

    return parsed !== undefined && parsed.toolCallId === item.toolCallId;
  }

  const outputSchema = getPortalToolOutputSchema(item.toolName);

  if (!outputSchema) {
    return false;
  }

  return (
    parsePortalToolResultItem({
      value: item,
      toolCallId: item.toolCallId,
      toolName: item.toolName,
      outputSchema,
    }) !== undefined
  );
}
